<section *ngIf="familyMode; else versionHistory" class="workflow-catalog" aria-labelledby="workflow-family-title" data-testid="workflow-family-catalog">
  <header class="workflow-catalog__header"><div><h2 id="workflow-family-title">Workflows</h2><p>Choose a workflow to review its versions or drafts.</p></div>
    <span *ngIf="familyPage && !isForbidden" data-family-count>{{ familyPage.totalCount }} workflows</span></header>
  <form class="workflow-family-search" (submit)="$event.preventDefault(); searchFamilies()">
    <label for="workflow-family-search">Search workflows</label>
    <input uiControl id="workflow-family-search" name="familySearch" [(ngModel)]="primaryFilterFormValues.search" [disabled]="loading" placeholder="Name, workflow, version, or draft">
    <button uiButton type="submit" [disabled]="loading">Search</button>
    <button uiButton type="button" [disabled]="loading" (click)="retry()">Refresh</button>
    <button *ngIf="query.search" uiButton type="button" [disabled]="loading" (click)="clearFamilySearch()">Clear search</button>
    <label><input type="checkbox" name="includeHiddenFamilies" data-include-hidden-families [ngModel]="query.includeHidden === 'true'" (ngModelChange)="setIncludeHidden($event)" [disabled]="loading || familyPage?.sharedVisibilityAvailable !== true"> Include hidden</label>
  </form>
  <ui-standard-state *ngIf="loading" kind="loading" [message]="familyPage ? 'Updating workflows. Current results remain available.' : 'Loading workflows.'" />
  <ui-standard-state *ngIf="error" [kind]="isForbidden ? 'forbidden' : 'error'" [message]="isForbidden ? 'You do not have permission to view workflows.' : 'Workflows could not be loaded. Try again.'" [recoveryLabel]="isForbidden ? undefined : 'Try again'" (recovery)="retry()" />
  <ui-standard-state *ngIf="!loading && !error && familyPage?.totalCount === 0" kind="empty" [message]="query.search ? 'No workflows match this search.' : 'No workflows are available yet.'" />
  <ui-standard-state *ngIf="!loading && !error && familyPage && familyPage.totalCount > 0 && familyPage.items.length === 0" kind="empty" message="This page no longer contains workflows." recoveryLabel="Return to first page" (recovery)="goToFirstPage()" />
  <div
    *ngIf="familyPage && familyRows.length && !isForbidden"
    class="workflow-catalog__grid"
    data-testid="workflow-family-grid"
    [attr.aria-busy]="loading"
  >
    <ui-grid
      width="fill"
      containerClass="no-panel workflow-registry-family-grid"
      [rowData]="familyRows"
      [rowHeight]="48"
      [columnDefs]="familyColumnDefs"
      [defaultColDef]="familyDefaultColDef"
      [agGridOptions]="familyGridOptions"
      [showPanelHeader]="false"
      [showBlankRowsOnLastPage]="false"
      [autoHidePaginator]="true"
    ></ui-grid>
  </div>
  <nav *ngIf="familyPage && familyPage.totalCount > 0 && !isForbidden" class="workflow-catalog__pagination" aria-label="Workflow family pages">
    <span>Page {{ familyPage.pageNumber }} · {{ familyPage.totalCount }} workflows</span>
    <div><button uiButton type="button" [disabled]="loading || familyPage.pageNumber <= 1" (click)="familyPageChange(-1)">Previous</button>
      <button uiButton type="button" [disabled]="loading || familyPage.pageNumber * familyPage.pageSize >= familyPage.totalCount" (click)="familyPageChange(1)">Next</button></div>
  </nav>
</section>
<ng-template #versionHistory>
<section
  class="workflow-catalog"
  aria-labelledby="workflow-catalog-title"
  data-testid="definition-list-section"
>
  <section
    *ngIf="!isForbidden && query.workflowCode as code"
    class="workflow-family-actions"
    data-family-history-context
    aria-label="Workflow family actions"
  >
    <header class="workflow-family-actions__identity">
      <h3>{{ family?.displayName || code }}</h3>
      <span>{{ code }}</span>
    </header>
    <div class="workflow-family-actions__groups">
      <fieldset
        class="workflow-family-actions__group"
        data-family-action-group="Browse"
        [disabled]="loading || !!error"
      >
        <legend>Browse</legend>
        <ui-button-group
          [buttonsConfig]="historyActionButtons"
          (buttonClick)="onHistoryActionButtonClick($event)"
        ></ui-button-group>
      </fieldset>
      <fieldset
        class="workflow-family-actions__group"
        data-family-action-group="Visibility"
        [disabled]="loading || !!error"
      >
        <legend>Visibility</legend>
        <phms-workflow-family-visibility
          [workflowCode]="code"
          [context]="visibilityContext(code)"
          [detail]="familyVisibility(code, family?.visibility)"
          [available]="familyVisibility(code, family?.visibility)?.isAvailable === true"
          [canAuthor]="canAuthor"
          [busy]="visibilityStates[code]?.busy === true"
          [blocked]="loading || !!error || visibilityStates[code]?.blocked === true"
          [retry]="visibilityStates[code]?.retry === true"
          [message]="visibilityStates[code]?.message ?? null"
          (changeRequested)="visibilityRequested.emit($event)"
          (refreshRequested)="visibilityRefreshRequested.emit(code)"
          (retryRequested)="visibilityRetryRequested.emit(code)"
        />
      </fieldset>
    </div>
  </section>
  <header class="workflow-catalog__header">
    <div>
      <h2 id="workflow-catalog-title">Workflow versions</h2>
      <p>
        Inspect a published version or create a draft to make changes. The
        current version keeps running while you edit.
      </p>
    </div>
    <span
      *ngIf="page && !isForbidden"
      class="workflow-catalog__count"
      data-testid="catalog-result-count"
    >
      {{ resultCountLabel }}
    </span>
  </header>

  <div
    class="workflow-catalog__filter-bar"
    role="group"
    aria-label="Workflow version filters"
  >
    <ui-form
      class="workflow-catalog__filters"
      [config]="primaryFilterFormConfig"
      [(formValues)]="primaryFilterFormValues"
      (submit)="onFilterSubmit($event)"
    ></ui-form>

    <div class="workflow-catalog__filter-tools">
      <button
        uiButton
        type="button"
        class="workflow-catalog__advanced-toggle"
        [attr.aria-expanded]="advancedFiltersOpen"
        aria-controls="workflow-catalog-advanced-filters"
        (click)="toggleAdvancedFilters()"
      >
        {{ advancedFiltersOpen ? "Fewer filters" : "More filters" }}
      </button>
      <ui-button-group
        class="workflow-catalog__filter-actions"
        [buttonsConfig]="filterActionButtons"
        (buttonClick)="onFilterActionButtonClick($event)"
      ></ui-button-group>
    </div>
  </div>

  <section
    id="workflow-catalog-advanced-filters"
    class="workflow-catalog__advanced-filters"
    aria-label="Additional workflow version filters"
    [hidden]="!advancedFiltersOpen"
  >
    <ui-form
      [config]="advancedFilterFormConfig"
      [(formValues)]="advancedFilterFormValues"
      (submit)="onFilterSubmit($event)"
    ></ui-form>
  </section>

  <ui-standard-state
    *ngIf="statusMessage"
    kind="loading"
    [message]="statusMessage"
    data-testid="loading-state"
  ></ui-standard-state>

  <ui-standard-state
    *ngIf="error"
    [kind]="isForbidden ? 'forbidden' : 'error'"
    [message]="errorMessage"
    [recoveryLabel]="isForbidden ? undefined : 'Try again'"
    [recoveryDisabled]="loading"
    (recovery)="retry()"
    data-testid="catalog-error"
  ></ui-standard-state>

  <p
    *ngIf="selectedVersionIsOffPage && page && !isForbidden"
    class="workflow-catalog__selection-note"
    data-testid="catalog-off-page-selection"
    role="status"
  >
    The open version is not on this page. Your selection is still available
    until you open another version.
  </p>

  <ui-standard-state
    *ngIf="!loading && page && page.totalCount === 0 && !error"
    kind="empty"
    [message]="
      hasActiveFilters
        ? 'No workflow versions match these filters. Clear a filter and try again.'
        : 'No workflow versions are available yet.'
    "
    [recoveryLabel]="hasActiveFilters ? 'Clear filters' : undefined"
    (recovery)="clearFilters()"
    data-testid="catalog-empty"
  ></ui-standard-state>

  <ui-standard-state
    *ngIf="
      !loading && page && page.totalCount > 0 && rows.length === 0 && !error
    "
    kind="empty"
    message="This page no longer contains versions."
    recoveryLabel="Return to first page"
    (recovery)="goToFirstPage()"
    data-testid="catalog-page-empty"
  ></ui-standard-state>

  <div
    *ngIf="page && rows.length > 0 && !isForbidden"
    class="workflow-catalog__grid"
    data-testid="workflow-catalog-grid"
    [attr.aria-busy]="loading"
  >
    <ui-grid
      width="fill"
      containerClass="no-panel workflow-registry-catalog-grid"
      [rowData]="rows"
      [columnDefs]="columnDefs"
      [defaultColDef]="defaultColDef"
      [agGridOptions]="gridOptions"
      [showPanelHeader]="false"
      [showBlankRowsOnLastPage]="false"
      [autoHidePaginator]="true"
    ></ui-grid>
  </div>

  <div
    *ngIf="page && page.totalCount > 0 && !isForbidden"
    class="workflow-catalog__pagination"
    role="group"
    aria-label="Workflow version pagination"
  >
    <div>
      <span data-testid="catalog-page-range" aria-live="polite">
        Page {{ page.pageNumber }} &middot; {{ pageRangeLabel }}
      </span>
      <span class="workflow-catalog__updated" *ngIf="asOfLabel"
        >Results as of {{ asOfLabel }}</span
      >
      <span class="visually-hidden">
        Versions are ordered by workflow code, then by newest published version.
        Column sorting affects only this loaded page.
      </span>
    </div>
    <ui-button-group
      role="group"
      aria-label="Workflow version pages"
      [buttonsConfig]="paginationButtons"
      (buttonClick)="onPaginationButtonClick($event)"
    ></ui-button-group>
  </div>
</section>

</ng-template>
